import { Injectable, NgZone } from '@angular/core';
import { Keyboard, KeyboardResize, KeyboardInfo } from '@capacitor/keyboard';
import { PluginListenerHandle } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';

export interface KeyboardConfig {
  containerId: string;
  inputId: string;
  transform: string;
}

@Injectable({
  providedIn: 'root'
})
export class KeyboardService {
  private keyboardShowListener: PluginListenerHandle | null = null;
  private keyboardHideListener: PluginListenerHandle | null = null;
  private isInitialized = false;
  private isKeyboardAvailable = false;
  private configs: KeyboardConfig[] = [];
  private activeTransforms = new Map<string, string>();
  private keyboardHeight = 0;

  constructor(private ngZone: NgZone) {}

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable('Keyboard')) {
      this.isInitialized = true;
      return;
    }

    this.isKeyboardAvailable = true;

    try {
      await Keyboard.setResizeMode({ mode: KeyboardResize.None });
      await Keyboard.setAccessoryBarVisible({ isVisible: true });

      document.addEventListener('focusin', (event: FocusEvent) => {
        const target = event.target as HTMLElement;
        console.log('👉 Focus event on:', target.tagName, 'ID:', target.id);
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
          this.checkAndApplyTransform(target);
        }
      });

      this.keyboardShowListener = await Keyboard.addListener('keyboardWillShow', (info: KeyboardInfo) => {
        this.handleKeyboardShow(info);
      });

      this.keyboardHideListener = await Keyboard.addListener('keyboardWillHide', () => {
        this.handleKeyboardHide();
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('KeyboardService initialization error:', error);
    }
  }

  setConfigs(configs: KeyboardConfig[]): void {
    this.configs = configs;
  }

  addConfig(config: KeyboardConfig): void {
    this.configs.push(config);
  }

  removeConfig(inputId: string): void {
    this.configs = this.configs.filter(c => c.inputId !== inputId);
  }

  private checkAndApplyTransform(input: HTMLElement): void {
    console.log('🔍 checkAndApplyTransform called, keyboardHeight:', this.keyboardHeight);
    if (!this.keyboardHeight) {
      console.log('❌ No keyboard height, returning');
      return;
    }

    const inputId = input.id || input.getAttribute('id');
    console.log('📝 Input ID:', inputId);
    console.log('📋 Available configs:', this.configs.length);
    
    const matchingConfig = this.configs.find(config => {
      return config.inputId === inputId;
    });

    if (!matchingConfig) {
      console.log('❌ No matching config found for input:', inputId);
      return;
    }

    console.log('✅ Found config:', matchingConfig);

    const containerElement = document.getElementById(matchingConfig.containerId);
    if (!containerElement) {
      console.log('❌ Container not found:', matchingConfig.containerId);
      return;
    }

    console.log('✅ Container found, applying transform:', matchingConfig.transform);
    containerElement.style.transform = matchingConfig.transform;
    containerElement.style.transition = 'transform 0.2s ease';
    this.activeTransforms.set(matchingConfig.containerId, matchingConfig.transform);
    console.log('✅ Transform applied successfully');
  }

  private handleKeyboardShow(info: KeyboardInfo): void {
    this.ngZone.run(() => {
      this.keyboardHeight = info.keyboardHeight;
      
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        this.checkAndApplyTransform(activeElement);
      }
    });
  }

  private handleKeyboardHide(): void {
    this.ngZone.run(() => {
      this.keyboardHeight = 0;
      
      this.activeTransforms.forEach((_, containerId) => {
        const container = document.getElementById(containerId);
        if (container) {
          container.style.transform = '';
          container.style.transition = 'transform 0.2s ease';
        }
      });
      
      this.activeTransforms.clear();
    });
  }

  async destroy(): Promise<void> {
    if (this.isKeyboardAvailable) {
      if (this.keyboardShowListener) {
        await this.keyboardShowListener.remove();
        this.keyboardShowListener = null;
      }
      if (this.keyboardHideListener) {
        await this.keyboardHideListener.remove();
        this.keyboardHideListener = null;
      }
    }

    this.isInitialized = false;
    this.isKeyboardAvailable = false;
    this.activeTransforms.clear();
  }
}
